import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, AlertCircle, ArrowLeft } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get("id") || "";
  const fromAdmin = params.get("from") === "admin";
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!id) {
      setState({ loading: false, data: null, error: "No ID provided" });
      return;
    }
    (async () => {
      try {
        const r = await fetch(
          `${BACKEND}/api/badge/verify/${encodeURIComponent(id)}`
        );
        const json = await r.json();
        setState({ loading: false, data: json, error: null });
      } catch (e) {
        setState({ loading: false, data: null, error: String(e) });
      }
    })();
  }, [id]);

  return (
    <div className="min-h-screen bg-slate-50 py-16 px-4">
      <div className="max-w-md mx-auto">
        {fromAdmin ? (
          <button
            type="button"
            onClick={() => navigate("/users/admin")}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brand-navy mb-6"
            data-testid="verify-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to admin registry
          </button>
        ) : (
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brand-navy mb-6"
            data-testid="verify-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> HepCURE home
          </Link>
        )}

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="bg-brand-navy px-6 py-4">
            <img
              src="/brand/hepcure-logo-white.png"
              alt="HepCURE"
              className="h-8 w-auto"
            />
            <p className="text-white/70 text-[10px] tracking-[0.22em] uppercase mt-1 font-semibold">
              Employee Credential Verification
            </p>
          </div>

          <div className="p-8" data-testid="verify-result">
            {state.loading && (
              <div className="text-center py-10 text-sm text-slate-500">
                Verifying credential…
              </div>
            )}

            {!state.loading && state.error && (
              <Result
                icon={<AlertCircle className="w-14 h-14 text-amber-500" />}
                title="Verification unavailable"
                subtitle={state.error}
                tone="amber"
              />
            )}

            {!state.loading &&
              state.data &&
              state.data.status === "verified" && (
                <>
                  <PhotoBlock data={state.data} />
                  <Result
                    icon={
                      <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                    }
                    title="Authorized HepCURE Employee"
                    subtitle="This credential has been verified"
                    tone="emerald"
                    testid="verify-status-verified"
                  />
                  <Details data={state.data} />
                </>
              )}

            {!state.loading &&
              state.data &&
              state.data.status === "expired" && (
                <>
                  <PhotoBlock data={state.data} muted />
                  <Result
                    icon={<XCircle className="w-12 h-12 text-red-500" />}
                    title="Credential expired"
                    subtitle={`This ID expired on ${state.data.expires}.`}
                    tone="red"
                    testid="verify-status-expired"
                  />
                  <Details data={state.data} muted />
                </>
              )}

            {!state.loading &&
              state.data &&
              state.data.status === "not_found" && (
                <Result
                  icon={<XCircle className="w-14 h-14 text-red-500" />}
                  title="Not recognized"
                  subtitle={`ID "${id}" was not found in the HepCURE employee registry.`}
                  tone="red"
                  testid="verify-status-not-found"
                />
              )}
          </div>

          <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-500">
              Confirmed against the official
              <br />
              HepCURE employee registry
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Employee photo displayed at the top of the credential card - full width. */
function PhotoBlock({ data, muted }) {
  if (!data?.photo_url) return null;
  return (
    <div className="mb-5">
      <div
        className={`w-full aspect-[4/5] border-2 border-brand-navy rounded-lg overflow-hidden bg-slate-100 shadow-sm ${
          muted ? "opacity-60" : ""
        }`}
        data-testid="verify-photo"
      >
        <img
          src={data.photo_url}
          alt={data.name || "Employee"}
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
}

function Result({ icon, title, subtitle, tone = "emerald", testid }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-100",
    red: "bg-red-50 border-red-100",
    amber: "bg-amber-50 border-amber-100",
  }[tone];
  return (
    <div
      className={`flex flex-col items-center text-center rounded-xl border p-5 ${bg}`}
      data-testid={testid}
    >
      {icon}
      <h2 className="text-base font-bold text-slate-900 mt-2">{title}</h2>
      <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
    </div>
  );
}

function Details({ data, muted }) {
  const opacity = muted ? "opacity-60" : "";
  return (
    <div className={`mt-6 ${opacity}`}>
      <div className="grid grid-cols-2 gap-4">
        <Row label="Name" value={data.name} bold />
        <Row label="Department" value={data.department} />
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] text-slate-500 font-semibold uppercase">
        {label}
      </div>
      <div
        className={`${
          bold ? "font-bold" : "font-medium"
        } text-sm text-slate-900`}
      >
        {value || "—"}
      </div>
    </div>
  );
}
